import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';

import EmptyModule from '@z-components/molecules/EmptyModule';
import StarRating from '@z-components/atoms/StarRating';
import ProBlogPostList from 'components/molecules/ProBlogPostList';
import ProProfileClientContent from '@z-components/molecules/ProProfileClientContent';
import ProProfileIntroduction from '@z-components/molecules/ProProfileIntroduction/ProProfileIntroduction';
import PhotoList from '@z-components/molecules/PhotoList';
import SelfIntroduction from '@z-components/molecules/SelfIntroduction';
import PricingMenuItemList from 'components/molecules/PricingMenuItemList';
import TabNavigation from 'components/molecules/TabNavigation';
import VerticalTabButtonGroup from 'components/molecules/VerticalTabButtonGroup';
import ClientReviewCardGroup from 'components/organisms/ClientReviewCardGroup';
import ProMatchingCriteria from 'modules/marketing-pages/components/ListPros/ProMatchingCriteria';
import ListCaseStudies from 'modules/users/modules/profiles/components/ListCaseStudies';
// import type { ListCaseStudiesProps } from 'modules/users/modules/profiles/components/ListCaseStudies';
import { useApi } from 'src/hooks';
import { addTransformToCloudinaryUrl } from 'utils/url';
import { JobProfile, ProProfilePro, PricingMenuItem, QuestionAnswer } from '../types';
import type { BlogPost } from 'modules/marketing-pages/marketing-page.types';

import styles from './ProProfileDetails.module.scss';
import JournalGroup from 'components/molecules/JournalGroup';
import { getInternalBlogPostsByJobTypeId } from 'modules/marketing-pages/service';

import {
  trackingEventForRequestCreation,
  GA4EventRequestCreation,
} from 'utils/eventTrackingForGA4';

interface Acc {
  [key: string]: Array<{ categoryTitle?: string; id: string; label: string }>;
}
interface Props {
  featureFlags: { isReviewsQAEnabled: boolean };
  internalBlogPosts: Array<BlogPost>;
  jobTypeId?: string;
  setJobTypeTitle: (jobTypeId: string) => void;
  user: ProProfilePro;
}

const getReducedJobProfiles = (jobProfiles: Array<JobProfile>) =>
  jobProfiles.reduce((acc: Acc, cur: JobProfile) => {
    let category2: string;

    if (!cur.categories) return acc;
    // handle category lists with no c3 value
    if (cur.categories.length > 2) {
      [, category2] = cur.categories;
    } else {
      [category2] = cur.categories;
    }

    // dedup verticals
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (acc[category2]) {
      const duplicate = acc[category2].find(
        (label: { label: string }) => label.label === cur.label
      );
      if (duplicate !== undefined) {
        return acc;
      }
    }

    const categoryTitle = cur.categoryTranslation.find((t) => t._id === category2)?.title;

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (acc[category2]) {
      return {
        ...acc,
        [category2]: [...acc[category2], { categoryTitle, id: cur.jobTypeId, label: cur.label }],
      };
    }
    return {
      ...acc,
      [category2]: [{ categoryTitle, id: cur.jobTypeId, label: cur.label }],
    };
  }, {});

const decideTabIndex = (currentJobTypeId: string | undefined, reducedJobProfiles: Acc) => {
  let tabIndex = 0;
  if (currentJobTypeId) {
    for (const [index, category] of Object.values(reducedJobProfiles).entries()) {
      if (category.some(({ id }) => id === currentJobTypeId)) {
        tabIndex = index + 1;
        break;
      }
    }
  }
  return tabIndex;
};

const ProProfileDetails: FC<Props> = ({
  featureFlags,
  internalBlogPosts,
  jobTypeId: currentJobTypeId,
  user: {
    availablePrefectures,
    blogPosts = [],
    currentTier = { id: 'new-user' },
    jobProfiles = [],
    name,
    pricingMenus,
    privateProfile = {},
    profile = {},
    proProfile,
    reviews = {
      counts: [0, 0, 0, 0, 0],
      list: [],
      total: 0,
    },
    slug,
  },
  setJobTypeTitle,
}) => {
  const {
    i18n: { language },
    t,
  } = useTranslation();
  const router = useRouter();
  const reducedJobProfiles = getReducedJobProfiles(jobProfiles);
  const [selectedTab, setSelectedTab] = useState(
    decideTabIndex(currentJobTypeId, reducedJobProfiles)
  );
  const [selectedVertical, setSelectedVertical] = useState(currentJobTypeId ?? '');
  const [areVerticalsCollapsed, setAreVerticalsCollapsed] = useState(Boolean(currentJobTypeId));
  const [visiblePricingMenu, setVisiblePricingMenu] = useState<Array<PricingMenuItem>>([]);
  const [currentBlogPosts, setCurrentBlogPosts] = useState<Array<BlogPost>>(internalBlogPosts);

  const filterPricingMenu = (jobTypeId: string) => {
    if (pricingMenus) {
      const pricingMenu = pricingMenus.find((item) => item.jobTypeId === jobTypeId);
      setVisiblePricingMenu(pricingMenu?.pricingMenus ?? []);
    } else {
      setVisiblePricingMenu([]);
    }
  };

  useEffect(() => {
    if (currentJobTypeId) {
      filterPricingMenu(currentJobTypeId);
      setSelectedVertical(currentJobTypeId);
      setSelectedTab(decideTabIndex(currentJobTypeId, reducedJobProfiles));
    }
  }, [pricingMenus, currentJobTypeId]);

  useEffect(() => {
    if (selectedVertical) {
      const jobType = jobProfiles.find((item) => item.jobTypeId === selectedVertical);
      const categories = jobType?.categories;

      trackingEventForRequestCreation(GA4EventRequestCreation.proProfileViewed, {
        jobTypeId: selectedVertical,
        // eslint-disable-next-line id-length
        C1: categories?.[0],
        // eslint-disable-next-line id-length
        C2: categories?.[1],
      });
    } else {
      trackingEventForRequestCreation(GA4EventRequestCreation.proProfileViewed);
    }
  }, [selectedVertical]);

  const handleTabClick = useCallback((tabIndex: number) => {
    setSelectedTab(tabIndex);
    if (tabIndex === 0) {
      setSelectedVertical('');
      history.pushState(
        '',
        '',
        `${window.location.origin}${language === 'en' ? '/en' : ''}/profile/${slug}/pro`
      );
    }
    setAreVerticalsCollapsed(false);
  }, []);

  const getTabsContent = () => {
    const handleVerticalSelection = async (jobTypeId: string) => {
      setJobTypeTitle(jobTypeId);
      filterPricingMenu(jobTypeId);

      setCurrentBlogPosts(jobTypeId ? await getInternalBlogPostsByJobTypeId({ jobTypeId }) : []);
    };

    return [
      {
        label: t('users:profile.description'),
      },
      ...Object.keys(reducedJobProfiles).map((category2) => ({
        content: (
          <VerticalTabButtonGroup
            areVerticalsCollapsed={areVerticalsCollapsed}
            background={true}
            items={reducedJobProfiles[category2]}
            language={language}
            slug={slug}
            // eslint-disable-next-line react/jsx-no-bind
            onItemClick={handleVerticalSelection}
            selectedItem={selectedVertical}
            // eslint-disable-next-line react/jsx-no-bind
            selectVertical={setSelectedVertical}
            setAreVerticalsCollapsed={setAreVerticalsCollapsed}
          />
        ),
        label: reducedJobProfiles[category2][0].categoryTitle,
        showArrow: true,
      })),
    ];
  };

  const getSelectedProfile = (jobTypeId: string) =>
    jobProfiles.find((jobProfile) => jobProfile.jobTypeId === jobTypeId);

  const jobTypeTitle = jobProfiles.find((type) => type.jobTypeId === selectedVertical)?.label;
  const jobTypeLabel = jobTypeTitle
    ? t('users:profile.jobType', {
        jobType: jobTypeTitle,
      })
    : undefined;
  const selectedVerticalReviewLength: number = selectedVertical
    ? reviews.list.filter((review) => review.jobTypeId === selectedVertical).length
    : 0;
  const selectedVerticalReviewRating: number = selectedVerticalReviewLength
    ? Number.parseFloat(
        (
          reviews.list
            .filter((review) => review.jobTypeId === selectedVertical)
            .reduce((acc, cur) => acc + cur.rating, 0) / selectedVerticalReviewLength
        ).toFixed(1)
      )
    : 0;
  const avatar = useMemo(
    () =>
      profile.avatar
        ? addTransformToCloudinaryUrl(profile.avatar, { dpr: '1.0', width: 150 })
        : undefined,
    [profile.avatar]
  );

  const questionAnswers: Array<QuestionAnswer> =
    getSelectedProfile(selectedVertical)?.questionAnswers ?? [];
  const filteredQuestionAnswers: Array<QuestionAnswer> = questionAnswers.filter(
    (questionAnswer) => questionAnswer.answer
  );

  const { applyId } = router.query;
  const [, markProWebsiteViewed] = useApi(
    {
      method: 'POST',
      url: `/apply/${applyId as string}/view-pro-website`,
    },
    { manual: true }
  );
  const handleProWebsiteUrlClick = useCallback(async () => {
    if (!applyId) {
      return;
    }
    await markProWebsiteViewed();
  }, [applyId, markProWebsiteViewed]);

  return (
    <div className={styles['pro-profile-detail']}>
      <div id="content" className={styles.content}>
        <ProProfileClientContent
          language={language as 'en' | 'ja'}
          proData={{
            availablePrefectures,
            avatar,
            location: proProfile.location?.title ?? '',
            tier: currentTier.id,
            title: name ?? '',
            verifiedBadges: {
              governmentId: privateProfile.governmentIdVerified ?? false,
              mail: privateProfile.emailVerified ?? false,
              phone: privateProfile.phoneNumberVerified ?? false,
            },
          }}
        />
      </div>
      <section id="category-navigation" className={styles['category-navigation']}>
        {jobProfiles.length > 0 && (
          <TabNavigation
            controlledSelection={selectedTab}
            list={getTabsContent()}
            onTabClick={handleTabClick}
          />
        )}
      </section>
      {!selectedVertical && (
        <>
          <ProProfileIntroduction
            isProView={false}
            language={language as 'en' | 'ja'}
            onWebsiteUrlClick={handleProWebsiteUrlClick}
            profile={proProfile}
          />
          <section id="case-study-in-intro" className={styles['case-study']}>
            <ListCaseStudies />
          </section>
        </>
      )}
      {selectedTab > 0 && selectedVertical && (
        <section id="job-profile">
          <div className={styles['review-count']}>
            <p>
              {t('users:profile.serviceProfileReviewCount', {
                total: selectedVerticalReviewLength,
              })}
            </p>
            <StarRating
              displayNumber={true}
              height={14}
              rating={getSelectedProfile(selectedVertical)?.rating ?? selectedVerticalReviewRating}
            />
          </div>
          <SelfIntroduction
            content={getSelectedProfile(selectedVertical)?.description}
            filteredQuestionAnswers={filteredQuestionAnswers}
            language={language as 'en' | 'ja'}
            title={getSelectedProfile(selectedVertical)?.title}
          />
          {
            <ProMatchingCriteria
              language={language as 'en' | 'ja'}
              matchingCriteria={getSelectedProfile(selectedVertical)?.matchingCriteria ?? []}
            />
          }
        </section>
      )}
      {selectedTab > 0 && selectedVertical && (
        <section id="pricing-menus">
          {name && jobTypeLabel && (
            <h3>{`${name}${jobTypeLabel}${t('users:profile.pricingMenu')}`}</h3>
          )}
          {visiblePricingMenu.length > 0 ? (
            <PricingMenuItemList
              items={visiblePricingMenu.map(({ description, key, price }) => ({
                description,
                key,
                priceRangeStart: price,
              }))}
            />
          ) : (
            <EmptyModule language={language as 'en' | 'ja'} text={t('users:profile.menu')} />
          )}
        </section>
      )}
      {selectedVertical && jobTypeTitle && (
        <section id="case-study-in-service-profile" className={styles['case-study']}>
          <ListCaseStudies
            title={{
              base: '事例',
              prefix: `${String(name)}の${String(jobTypeTitle)}の`,
            }}
          />
        </section>
      )}
      {proProfile.photos && proProfile.photos.length > 0 && selectedVertical && (
        <>
          <section id="photos-desktop" className={styles['photos-desktop']}>
            <PhotoList
              name={name}
              translations={{
                jobType: jobTypeLabel,
                noCaption: t('users:profile.noCaption'),
                photos: t('users:profile.photos'),
                seeMoreLabel: t('common:seeMore'),
              }}
              photos={proProfile.photos}
              rowLimit={10}
            />
          </section>
          <section id="photos-mobile" className={styles['photos-mobile']}>
            <PhotoList
              name={name}
              translations={{
                jobType: jobTypeLabel,
                noCaption: t('users:profile.noCaption'),
                photos: t('users:profile.photos'),
              }}
              photos={proProfile.photos}
              photoMargin={16}
            />
          </section>
        </>
      )}
      {selectedVertical && (
        <section id="reviews">
          <ClientReviewCardGroup
            isReviewsQAEnabled={featureFlags.isReviewsQAEnabled}
            reviews={reviews.list
              .filter((review) => review.jobTypeId === selectedVertical)
              .map((review) => ({
                shortName: review.shortName ?? '',
                ...review,
              }))}
            title={{
              en: `${name ?? ''}'s ${jobTypeTitle ?? ''} Reviews`,
              ja: `${name ?? ''}の${jobTypeTitle ?? ''}に関する評判`,
            }}
          />
        </section>
      )}
      {!selectedVertical && blogPosts.length > 0 && (
        <section id="blog-posts">
          <h3 className={styles['title']}>
            {t('marketingPages:proBlogPostSection.proProfileTitle')}
          </h3>
          <ProBlogPostList posts={blogPosts} />
        </section>
      )}
      {selectedVertical && currentBlogPosts.length > 0 && (
        <section id="internal-blog-posts">
          <h3>
            {t('marketingPages:latestArticleSectionTitle', {
              jobType: jobTypeTitle,
            })}
          </h3>
          <JournalGroup posts={currentBlogPosts} />
        </section>
      )}
    </div>
  );
};

export default ProProfileDetails;
export type { Props as ProProfileDetailsProps };
